import { uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { AutoTable } from '../../../../../components/auto-table';
import { CustomPopover } from '../../../../../components/custom-popover';
import { CommonTag } from '../../../../../components/tag';
import getFormatMessage from '../../../../../locales/get-format-message';
import { IApiGCloudGameItem } from '../../../../../model/api-modules';

import styles from './index.module.scss';

interface IProps {
  readonly loading?: boolean;
  readonly rawList: readonly IApiGCloudGameItem[];
  readonly onChange: (
    list: IApiGCloudGameItem[],
    allList: IApiGCloudGameItem[]
  ) => void;
}

export const NoticeSDKList = (props: IProps) => {
  const { loading, rawList, onChange } = props;
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectRows, setSelectRows] = useState<
    ReadonlyArray<IApiGCloudGameItem>
  >([]);

  useEffect(() => {
    if (!loading) {
      setSelectRows([...rawList]);
    }
  }, [loading]);

  return (
    <div>
      <AutoTable
        loading={loading}
        list={rawList}
        tableExtension={{
          scroll: { maxY: 'calc(100vh - 280px)' },

          rowKey: 'id',

          rowSelection: {
            selectedRowKeys: selectRows.map((item) => item.id),

            onChange: (
              _selectKeys: readonly (string | number)[],
              selectRows
            ) => {
              setSelectRows([...selectRows]);
              onChange([...selectRows], [...rawList]);
            },
          },
          onChange: (pagination) => {
            if (!pagination) {
              return;
            }
            setPage(pagination.current);
            setPageSize(pagination.pageSize || 10);
          },
          pagination: {
            current: page,
            total: rawList.length,
            pageSize,
            showTotal: () => {
              const gameCount = uniq(
                selectRows.map((row) => row.gameId)
              ).length;

              const memberCount = uniq(
                selectRows
                  .map((row) => row.memberList.map((member) => member.email))
                  .flat()
              ).length;
              return (
                <div className={styles.tableTotal}>
                  <span>
                    {getFormatMessage('total_with_checked_items', {
                      params: {
                        total: rawList.length,
                        count: selectRows.length,
                      },
                    })}
                  </span>
                  {!!gameCount && (
                    <span>
                      (
                      <span className={styles.summaryText}>
                        {getFormatMessage('notice_selected_summary_table', {
                          params: {
                            gameCount,
                            memberCount,
                          },
                        })}
                      </span>
                      )
                    </span>
                  )}
                </div>
              );
            },
          },
        }}
        configFields={[
          {
            key: 'gameId',
            name: getFormatMessage('gameID'),
            tableExtension: {
              width: 80,
            },
          },
          {
            key: 'gameName',
            name: getFormatMessage('game_name'),
            tableExtension: {
              width: 100,
            },
          },
          {
            key: 'downloadUser',
            name: getFormatMessage('notice_creator'),
            tableExtension: {
              width: 90,
            },
          },
          {
            key: 'gcloudVersion',
            name: getFormatMessage('notice_gcloud_sdk_ver'),
            tableExtension: {
              width: 132,
            },
          },
          {
            key: 'sdkVersion',
            name: getFormatMessage('notice_component_ver'),
            tableExtension: {
              width: 320,
              rowTdClassName: styles.combinedCell,
              renderRow(key, val, record) {
                const getAllValues = () => {
                  return (
                    <div>
                      {record.sdkList.map((sdk) => {
                        return (
                          <div>
                            {sdk.sdkName}/{sdk.sdkVersion}
                          </div>
                        );
                      })}
                    </div>
                  );
                };
                return (
                  <CustomPopover
                    content={getAllValues()}
                    overlayClassName={styles.sdkPopover}
                  >
                    <div className={styles.subList}>
                      {[...record.sdkList].slice(0, 2).map((sdk) => (
                        <CommonTag>
                          {sdk.sdkName}/{sdk.sdkVersion}
                        </CommonTag>
                      ))}

                      {record.sdkList.length > 2 && (
                        <CommonTag>+{record.sdkList.length - 2}</CommonTag>
                      )}
                    </div>
                  </CustomPopover>
                );
              },
            },
          },
          {
            key: 'memberCount',
            name: getFormatMessage('notice_member_count'),
            tableExtension: {
              renderRow(key, val, record) {
                return record.memberList.length;
              },
            },
          },
          {
            key: 'isBindKpiCode',
            name: getFormatMessage('notice_kpi_binding'),
            tableExtension: {
              renderRow(key, val) {
                return val ? getFormatMessage('yes') : getFormatMessage('no');
              },
            },
          },
        ]}
      />
    </div>
  );
};
